import { type ReactiveController, type ReactiveElement } from "lit";

/**
 * Reactive controller for handling iOS virtual keyboard behavior.
 *
 * iOS Safari doesn't resize the window when the keyboard appears - it uses the Visual Viewport API instead.
 * This causes layout issues with fixed/fullscreen containers when a text input is focused.
 *
 * This controller:
 * - Detects iOS devices
 * - Subscribes to `visualViewport.resize` events
 * - Provides reactive state for `isKeyboardVisible`, `viewportHeight`, and `keyboardHeight`
 * - Helps fix scroll restoration after keyboard dismissal
 *
 * @example
 * ```typescript
 * class MyComponent extends LitElement {
 *   private keyboardController = new IOSKeyboardController(this);
 *
 *   render() {
 *     const containerStyle = IOSKeyboardController.isIOS && this.keyboardController.isKeyboardVisible
 *       ? `height: ${this.keyboardController.viewportHeight}px`
 *       : '';
 *     return html`<div class="container" style=${containerStyle}>...</div>`;
 *   }
 * }
 * ```
 */
export class IOSKeyboardController implements ReactiveController {
  /**
   * Detect iOS devices using platform/userAgent check.
   * Same pattern used in BodyScrollManager.
   */
  static readonly isIOS =
    /iP(ad|hone|od)/.test(navigator.platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);

  /**
   * Shared scroll lock state - prevents multiple instances from conflicting
   */
  private static scrollLockRefCount = 0;
  private static scrollLockHandler: ((e: TouchEvent) => void) | null = null;

  private _isKeyboardVisible = false;
  private _viewportHeight = 0;
  private _keyboardHeight = 0;
  private _offsetTop = 0;
  private initialHeight = 0;
  private hasScrollLockRef = false;

  /**
   * Threshold in pixels for keyboard detection.
   * A viewport height reduction greater than this indicates the keyboard is open.
   * 150px avoids false positives from URL bar changes while catching keyboard.
   */
  private static readonly KEYBOARD_THRESHOLD = 150;

  constructor(private host: ReactiveElement) {
    this.host.addController(this);
  }

  get isKeyboardVisible(): boolean {
    return this._isKeyboardVisible;
  }

  get viewportHeight(): number {
    return this._viewportHeight;
  }

  get keyboardHeight(): number {
    return this._keyboardHeight;
  }

  /**
   * The visual viewport's offset from the top of the layout viewport.
   * When the keyboard appears, iOS Safari scrolls the visual viewport down,
   * and this value increases. Use this to position fixed containers correctly.
   */
  get offsetTop(): number {
    return this._offsetTop;
  }

  hostConnected(): void {
    if (!IOSKeyboardController.isIOS || !window.visualViewport) return;

    this.initialHeight = window.visualViewport.height;
    this._viewportHeight = this.initialHeight;

    window.visualViewport.addEventListener("resize", this.handleViewportChange);
    window.visualViewport.addEventListener("scroll", this.handleViewportChange);
    window.addEventListener("orientationchange", this.handleOrientationChange);
  }

  hostDisconnected(): void {
    if (!IOSKeyboardController.isIOS || !window.visualViewport) return;

    window.visualViewport.removeEventListener("resize", this.handleViewportChange);
    window.visualViewport.removeEventListener("scroll", this.handleViewportChange);
    window.removeEventListener("orientationchange", this.handleOrientationChange);

    if (this.hasScrollLockRef) {
      this.releaseScrollLock();
    }
  }

  private handleOrientationChange = (): void => {
    // After orientation change, reset initialHeight once the viewport settles.
    // Without this, the height difference between portrait and landscape
    // is mistaken for an open keyboard, which activates scroll lock.
    setTimeout(() => {
      if (!window.visualViewport) return;
      this.initialHeight = window.visualViewport.height;
      this.handleViewportChange();
    }, 300);
  };

  private handleViewportChange = (): void => {
    if (!window.visualViewport) return;

    const currentHeight = window.visualViewport.height;
    const heightDiff = this.initialHeight - currentHeight;

    this._viewportHeight = currentHeight;
    this._keyboardHeight = Math.max(0, heightDiff);
    this._offsetTop = window.visualViewport.offsetTop;
    this._isKeyboardVisible = heightDiff > IOSKeyboardController.KEYBOARD_THRESHOLD;

    // Enable/disable scroll lock based on keyboard visibility
    if (this._isKeyboardVisible && !this.hasScrollLockRef) {
      this.acquireScrollLock();
    } else if (!this._isKeyboardVisible && this.hasScrollLockRef) {
      this.releaseScrollLock();
    }

    this.host.requestUpdate();
  };

  /**
   * Find scrollable parent traversing through shadow DOM boundaries.
   * Standard `closest()` doesn't work across shadow roots.
   */
  private findScrollableParent(element: HTMLElement | null): HTMLElement | null {
    const scrollableSelectors = [".messages-container", ".modal-body"];

    let current: HTMLElement | null = element;
    while (current) {
      // Check if current element matches scrollable selector
      for (const selector of scrollableSelectors) {
        if (current.matches?.(selector)) {
          return current;
        }
      }

      // Try to find in same DOM context first
      for (const selector of scrollableSelectors) {
        const found = current.closest?.(selector);
        if (found) {
          return found as HTMLElement;
        }
      }

      // Traverse up: if we hit a shadow root, go to its host
      const parent = current.parentElement;
      if (parent) {
        current = parent;
      } else {
        // Check if we're inside a shadow root
        const rootNode = current.getRootNode();
        if (rootNode instanceof ShadowRoot) {
          current = rootNode.host as HTMLElement;
        } else {
          current = null;
        }
      }
    }
    return null;
  }

  private preventTouchMove = (e: TouchEvent): void => {
    // Use composedPath()[0] to get the actual inner element across shadow DOM boundaries.
    // e.target is retargeted to the outermost shadow host at the document level,
    // which prevents findScrollableParent from locating containers inside shadow roots.
    const target = (e.composedPath()[0] as HTMLElement) || (e.target as HTMLElement);
    const scrollableParent = this.findScrollableParent(target);
    if (scrollableParent) {
      // Allow scroll if the element has scrollable content
      const isScrollable = scrollableParent.scrollHeight > scrollableParent.clientHeight;
      if (isScrollable) return;
    }
    e.preventDefault();
  };

  /**
   * Acquire a reference to the shared scroll lock.
   * The actual event listener is only added on the first acquisition.
   */
  private acquireScrollLock(): void {
    if (this.hasScrollLockRef) return;

    this.hasScrollLockRef = true;
    IOSKeyboardController.scrollLockRefCount++;

    // Only add the listener once (when first instance acquires lock)
    if (IOSKeyboardController.scrollLockRefCount === 1) {
      IOSKeyboardController.scrollLockHandler = this.preventTouchMove;
      document.addEventListener("touchmove", IOSKeyboardController.scrollLockHandler, { passive: false });
    }
  }

  /**
   * Release this instance's reference to the shared scroll lock.
   * The actual event listener is only removed when all references are released.
   */
  private releaseScrollLock(): void {
    if (!this.hasScrollLockRef) return;

    this.hasScrollLockRef = false;
    IOSKeyboardController.scrollLockRefCount--;

    // Only remove the listener when no more instances need it
    if (IOSKeyboardController.scrollLockRefCount === 0 && IOSKeyboardController.scrollLockHandler) {
      document.removeEventListener("touchmove", IOSKeyboardController.scrollLockHandler);
      IOSKeyboardController.scrollLockHandler = null;
    }
  }

  /**
   * Force reflow on a scrollable container to fix iOS Safari scroll lock after keyboard dismissal.
   * Call this from an input blur handler.
   *
   * Uses double RAF to ensure layout completes before restoring scroll.
   *
   * @param container - The scrollable container element
   */
  static fixScrollAfterKeyboardDismiss(container: Element | null | undefined): void {
    if (!container || !IOSKeyboardController.isIOS) return;

    const htmlContainer = container as HTMLElement;
    const scrollTop = htmlContainer.scrollTop;

    requestAnimationFrame(() => {
      htmlContainer.style.overflowY = "hidden";
      requestAnimationFrame(() => {
        htmlContainer.style.overflowY = "auto";
        htmlContainer.scrollTop = scrollTop;
      });
    });
  }

  /**
   * Scroll to bottom after a small delay to ensure input stays visible.
   * Call this from an input focus handler.
   *
   * @param container - The scrollable container element
   * @param delay - Delay in ms before scrolling (default 100ms for keyboard animation)
   */
  static scrollToBottomAfterFocus(container: Element | null | undefined, delay = 100): void {
    if (!container) return;

    setTimeout(() => {
      container.scrollTop = container.scrollHeight;
    }, delay);
  }
}
